import {queries} from '@/shared/integrations/zero'
import {Customer} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {ListCustomersInput} from '@cypro/engine/queries'
import {useCallback, useMemo, useState} from 'react'

export type CustomerStartRow = Pick<Customer, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteCustomers({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListCustomersInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.name, filters?.status, limit, search]
  )

  const getPageQuery: GetPageQuery<Customer, CustomerStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {id: start.id, direction: dir, createdAt: start.createdAt, name: start.name}
          : undefined

      return {
        query: queries.customers.listCustomers({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {name: search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Customer> = useCallback(
    ({id}) => ({
      query: queries.customers.getCustomerById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Customer): CustomerStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: Customer) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
